import { useId, type CSSProperties } from 'react'
import { cn } from './cn'
import { NEURAL_LINES, NEURAL_NODES, NEURAL_TILE } from './neural-data'

/** REDESIGN 4: the pattern stays only on 404, maintenance and empty states, at 4 percent at most. */
const NEURAL_MAX_OPACITY = 0.04

export interface NeuralPatternProps {
  /** 0.04 by default; larger values are capped at NEURAL_MAX_OPACITY (older callers passed 0.12 on heroes). */
  opacity?: number
  /** Tile scale (1 = 320 x 200 px). */
  scale?: number
  /** Fill the nearest positioned parent (default true). */
  fill?: boolean
  /** Fade out towards the edges with a radial mask. */
  fade?: boolean
  className?: string
  style?: CSSProperties
}

/** Scattered nodes joined by 1px lines in currentColor. Decorative. */
export function NeuralPattern({ opacity = NEURAL_MAX_OPACITY, scale = 1, fill = true, fade = false, className, style }: NeuralPatternProps) {
  const id = `mgs-neural-${useId().replace(/:/g, '')}`
  const shown = Math.min(Math.max(opacity, 0), NEURAL_MAX_OPACITY)
  const mask = fade ? 'radial-gradient(ellipse at center, #000 30%, transparent 75%)' : undefined
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      className={cn('pointer-events-none select-none', fill && 'absolute inset-0 size-full', className)}
      style={{ opacity: shown, maskImage: mask, WebkitMaskImage: mask, ...style }}
    >
      <defs>
        <pattern
          id={id}
          width={NEURAL_TILE.width}
          height={NEURAL_TILE.height}
          patternUnits="userSpaceOnUse"
          patternTransform={scale === 1 ? undefined : `scale(${scale})`}
        >
          <g stroke="currentColor" strokeWidth="1" fill="none">
            {NEURAL_LINES.map(([x1, y1, x2, y2], i) => (
              <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />
            ))}
          </g>
          <g fill="currentColor">
            {NEURAL_NODES.map(([cx, cy, r], i) => (
              <circle key={i} cx={cx} cy={cy} r={r} />
            ))}
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  )
}
